/**
 * Flow tag metric resolution — reads an item's OPTIMIZER metrics (the same
 * values that fed its raw score), resolves the applicable threshold (campaign
 * override, else country override, else the condition's value) and applies the
 * comparison operator. Mirrors the performance-tag condition semantics.
 * Also bakes overrides into a rule set (resolveRulesForContext) so callers that
 * know the campaign/country once can hand the evaluator context-free rules.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  hasAnyOverride,
  resolveOverrideEntry,
  resolveOverrideThreshold,
  type ResolvedOverrideEntry,
  type TagOverrideContext,
} from "../tag-rules/override-context";
import {
  isRankingCondition,
  type FlowTagCondition,
  type FlowTagItem,
  type FlowTagMetricCondition,
  type FlowTagRule,
  type TagComparisonOperator,
} from "./types";

const FIELD_ALIASES: Record<string, string> = {
  ftd: "customConversions7",
  visits: "uniqueVisits",
  registrations: "customConversions6",
  ROI: "roi",
  EPL: "epl",
};

/**
 * Metrics for the window the rule asked for — and ONLY that window.
 *
 * A rule that names a window must be judged on it: falling through to another
 * window's numbers made a "Last 7 days" rule silently read 30-day stats, so the
 * verdict disagreed with both the tag's own label and the hover card. An item
 * absent from its window genuinely has no traffic there and reads as all-zero,
 * exactly like an item the cube never saw.
 *
 * Only a rule that names NO window at all keeps the legacy "use whatever single
 * window is loaded" behaviour.
 */
export function pickMetrics(
  item: FlowTagItem,
  cubeKey?: string,
): Record<string, number> {
  const byRange = item.metricsByDateRange ?? {};
  const breakdown = item.breakdown ?? [];
  if (cubeKey) {
    const fromBreakdown = breakdown.find(
      (b) => b.dateRange === cubeKey && b.metrics,
    )?.metrics;
    return byRange[cubeKey] ?? fromBreakdown ?? {};
  }
  const keys = Object.keys(byRange);
  if (keys.length) return byRange[keys[0]];
  return breakdown[0]?.metrics ?? {};
}

/** Field read with alias + impressions→sends fallback; missing → undefined. */
export function readMetricField(
  metrics: Record<string, number>,
  field: string,
): number | undefined {
  const key = FIELD_ALIASES[field] ?? field;
  if (key === "impressions") {
    const imp = metrics.impressions;
    if (typeof imp === "number" && !Number.isNaN(imp)) return imp;
    const sends = metrics.sends;
    return typeof sends === "number" && !Number.isNaN(sends) ? sends : undefined;
  }
  const v = metrics[key];
  return typeof v === "number" && !Number.isNaN(v) ? v : undefined;
}

export function flowTagMetricValue(
  item: FlowTagItem,
  field: string,
  cubeKey?: string,
): number {
  return readMetricField(pickMetrics(item, cubeKey), field) ?? 0;
}

/**
 * Threshold for this condition under `ctx` — campaign, else country, else the
 * condition's own value. Delegates to the shared resolver so the FO evaluator,
 * the performance-tag evaluator and the browser copy cannot disagree.
 */
export function resolveThreshold(
  c: FlowTagMetricCondition,
  ctx: TagOverrideContext,
): number {
  return resolveOverrideThreshold({
    value: c.value,
    compareTo: c.compareTo,
    countryThresholds: c.countryThresholds,
    campaignThresholds: c.campaignThresholds,
    ctx,
  });
}

/** The winning override ENTRY for this condition under `ctx` — a comparison
 *  or a pinned number, campaign → country → the condition's own default. */
export function resolveThresholdEntry(
  c: FlowTagMetricCondition,
  ctx: TagOverrideContext,
): ResolvedOverrideEntry {
  return resolveOverrideEntry({
    value: c.value,
    compareTo: c.compareTo,
    countryThresholds: c.countryThresholds,
    campaignThresholds: c.campaignThresholds,
    ctx,
  });
}

/** True when a rule has at least one metric condition carrying overrides. */
function hasOverrides(rule: FlowTagRule): boolean {
  return (rule.conditions ?? []).some(
    (c) => !isRankingCondition(c) && hasAnyOverride(c),
  );
}

/** Bake one condition's threshold for `ctx`; nothing to bake -> same reference. */
function bakeCondition(
  c: FlowTagCondition,
  ctx: TagOverrideContext,
): FlowTagCondition {
  if (isRankingCondition(c) || !hasAnyOverride(c)) return c;
  const {
    countryThresholds: _country,
    campaignThresholds: _campaign,
    compareTo: _compareTo,
    ...rest
  } = c;
  const entry = resolveThresholdEntry(c, ctx);
  // A compare entry bakes its comparison; a value entry bakes the number and
  // DROPS any base comparison — a pinned override replaces it entirely.
  return entry.compareTo
    ? { ...rest, value: c.value, compareTo: entry.compareTo }
    : { ...rest, value: entry.value };
}

/** Nothing to resolve against — leave the rules exactly as stored. */
function isEmptyContext(ctx: TagOverrideContext): boolean {
  const country = String(ctx.countryKey ?? "").trim();
  const campaign = String(ctx.campaignId ?? "").trim();
  return !country && !(campaign && ctx.provider);
}

/**
 * Bake overrides into a rule set, so the evaluator needs no context:
 * `evaluateFlowTags(items, resolveRulesForContext(rules, ctx))` is identical to
 * `evaluateFlowTags(items, rules, ctx)` — the context only ever reaches
 * resolveThreshold, and a baked condition carries no overrides left to resolve.
 *
 * Rules WITHOUT overrides come back BY REFERENCE, so a rule set that uses none
 * is provably untouched. Never mutates the input. Idempotent: baked rules have
 * neither override array, so re-baking under any context is a no-op.
 */
export function resolveRulesForContext(
  rules: ReadonlyArray<FlowTagRule>,
  ctx: TagOverrideContext,
): FlowTagRule[] {
  if (isEmptyContext(ctx)) return [...rules];
  return rules.map((rule) =>
    hasOverrides(rule)
      ? { ...rule, conditions: rule.conditions.map((c) => bakeCondition(c, ctx)) }
      : rule,
  );
}

/**
 * @deprecated Country-only shorthand for {@link resolveRulesForContext}. Kept
 * so callers that genuinely have no campaign in scope stay readable.
 */
export function resolveRulesForCountry(
  rules: ReadonlyArray<FlowTagRule>,
  countryKey: string,
): FlowTagRule[] {
  return resolveRulesForContext(rules, { countryKey });
}

export function compareMetric(
  actual: number,
  operator: TagComparisonOperator,
  threshold: number,
): boolean {
  if (!Number.isFinite(threshold)) return false;
  switch (operator) {
    case "gte":
      return actual >= threshold;
    case "lte":
      return actual <= threshold;
    case "gt":
      return actual > threshold;
    case "lt":
      return actual < threshold;
    case "eq":
      return actual === threshold;
    default:
      return false;
  }
}
